import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { FaPaperPlane, FaTimes } from "react-icons/fa";
import {
  MODULE_PAGE_SHELL_STYLE,
  TASK_PAGE_SHELL_CLASS,
} from "../../TaskManagement/workspaceConstants";
import Paginations from "../../GenericComponents/Pagination";
import {
  exportApplicationsCsv,
  fetchAllApplicationsMatchingFilters,
  fetchBranches,
  fetchCoursesForProvider,
  fetchExternalAgents,
  fetchProviders,
  addApplicationToQueue,
  addApplicationsToQueue,
  discontinueApplication,
  reactivateApplication,
  markCommissionStudentCompleted,
  revertCommissionStudentCompleted,
  submitCommissionFinalReview,
  approveCommissionFinalReview,
  returnCommissionFromFinalReview,
  removeApplicationFromQueue,
  runCommissionBulkWorkflow,
} from "./commissionsApi";
import CommissionsBulkActions from "./CommissionsBulkActions";
import CommissionsHeader from "./CommissionsHeader";
import CommissionsReportTabs from "./CommissionsReportTabs";
import CommissionsQueueTabs from "./CommissionsQueueTabs";
import CommissionsSearchFilters from "./CommissionsSearchFilters";
import CommissionsTable from "./CommissionsTable";
import CommissionsColumnSettings from "./CommissionsColumnSettings";
import CreateInquiryBatchModal from "./CreateInquiryBatchModal";
import { CommissionsSingleSelect } from "./CommissionsMultiSelect";
import InquiryFillModal from "./InquiryFillModal";
import InquiryBatchesPanel from "./InquiryBatchesPanel";
import {
  countActiveFilters,
  getCommissionsFilterQueryState,
  useCommissionsFilters,
} from "./useCommissionsFilters";
import { useCommissionsData } from "./useCommissionsData";
import { useCommissionsColumnVisibility } from "./useCommissionsColumnVisibility";
import {
  showClientAddedToast,
  confirmClientAction,
  confirmRemoveFromCommissionable,
  showClientRemovedFromCommissionableToast,
  showClientActionErrorToast,
} from "./commissionsToast";
import { getCommissionRowKey, getCommissionCourseLabel } from "./commissionRowUtils";
import { getReactivateStatusMeta } from "./commissionsConstants";
import {
  canViewCommissionModule,
  canViewCommissionableStudentsTab,
  canRemoveFromCommissionableStudents,
  canBulkCommissionCreateInquiryBatch,
} from "../../../utilis/invoicePermissionHelpers";
import { shouldApplyCommissionsSearchInput } from "./commissionsSearchUtils";
import { isCommissionRowSelectableForBulk } from "./commissionRowSelection";

import "../../../styles/tailwind.css";
import "./commissions-responsive.css";

/** Debounce a value until the user stops changing it for `delayMs`. */
function useDebouncedValue(value, delayMs = 1000) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Normalize search for the API:
 * - empty → clear search
 * - numeric ID / DOB-shaped → apply immediately
 * - 1–2 chars (other) → keep previous (do not fire yet)
 * - ≥ 3 chars → use the term
 */
function resolveSearchTerm(rawInput, previousAppliedSearch) {
  const term = (rawInput ?? "").trim();
  if (term.length === 0) return "";
  if (shouldApplyCommissionsSearchInput(term)) return term;
  return previousAppliedSearch ?? "";
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function mapReportParam(reportParam) {
  if (!reportParam) return null;
  // Legacy Inquiry Queue URLs → Commissionable (batch is created from there)
  if (reportParam === "inquiry" || reportParam === "no_commission") {
    return "commission";
  }
  if (reportParam === "inquiry_batches" || reportParam === "batches") {
    return "inquiry_batches";
  }
  if (reportParam === "commission" || reportParam === "general") {
    return reportParam;
  }
  return null;
}

function CommissionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    draft,
    applied,
    filtersOpen,
    setFiltersOpen,
    setDraft,
    setDraftField,
    clearFilters,
    setReport,
    setCommissionView,
    toggleSort,
    removeChip,
    setProviderFilter,
  } = useCommissionsFilters();

  // ── Search is independent of filters ─────────────────────────────────────
  // Immediate input value (responsive UI)
  const [searchInput, setSearchInput] = useState("");
  // Debounced only after user pauses ~1s
  const debouncedSearchInput = useDebouncedValue(searchInput, 1000);

  // Effective search sent to the API (min 3 chars, or empty to clear)
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");

  useEffect(() => {
    const next = resolveSearchTerm(debouncedSearchInput, appliedSearchTerm);
    setAppliedSearchTerm((prev) => (prev === next ? prev : next));
  }, [debouncedSearchInput, appliedSearchTerm]);

  // Filters + independent search for data fetching
  const effectiveApplied = useMemo(
    () => ({
      ...applied,
      searchTerm: appliedSearchTerm,
    }),
    [applied, appliedSearchTerm],
  );

  const {
    loading,
    applications,
    pagination,
    intakes,
    viewCounts,
    handlePageChange,
    handlePerPageChange,
    resetPagination,
    refetch,
  } = useCommissionsData(effectiveApplied);

  // Reset pagination when the debounced search actually changes
  useEffect(() => {
    resetPagination();
  }, [appliedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const appliedFiltersKey = useMemo(
    () => JSON.stringify(getCommissionsFilterQueryState(applied)),
    [applied],
  );
  const appliedFiltersKeyRef = useRef(appliedFiltersKey);
  useEffect(() => {
    if (appliedFiltersKeyRef.current === appliedFiltersKey) {
      return;
    }
    appliedFiltersKeyRef.current = appliedFiltersKey;
    resetPagination();
    setSelectedMap(new Map());
    setSelectionScope("none");
    setProviderSelectionMeta(null);
  }, [appliedFiltersKey, resetPagination]);

  const [draftCoursesList, setDraftCoursesList] = useState([]);
  const [providersList, setProvidersList] = useState([]);
  const [processingAgentsList, setProcessingAgentsList] = useState([]);
  const [referringAgentsList, setReferringAgentsList] = useState([]);
  const [branchesList, setBranchesList] = useState([]);
  const [actionLoadingKey, setActionLoadingKey] = useState(null);
  const [selectedMap, setSelectedMap] = useState(() => new Map());
  const [selectionScope, setSelectionScope] = useState("none");
  const [providerSelectionMeta, setProviderSelectionMeta] = useState(null);
  const [selectingAllProvider, setSelectingAllProvider] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [editApplication, setEditApplication] = useState(null);

  const canCommissionStudents = canViewCommissionableStudentsTab();
  const canCreateInquiryBatch = canBulkCommissionCreateInquiryBatch();

  const appliedProviderName = useMemo(() => {
    if (!applied.providerId) return "";
    const match = providersList.find(
      (provider) => String(provider.id) === String(applied.providerId),
    );
    return match?.name || `Provider ${applied.providerId}`;
  }, [applied.providerId, providersList]);

  const isCommissionTab = applied.report === "commission";
  const isInquiryBatchesTab = applied.report === "inquiry_batches";
  const columnReport = isCommissionTab ? "commission" : "general";
  const { visibleKeys, visibleColumns, setVisibleKeys } =
    useCommissionsColumnVisibility(columnReport);

  // Legacy Inquiry Queue report id → Commissionable (only when allowed)
  useEffect(() => {
    if (applied.report === "no_commission") {
      setReport(canCommissionStudents ? "commission" : "general");
      resetPagination();
    }
  }, [
    applied.report,
    canCommissionStudents,
    setReport,
    resetPagination,
  ]);

  useEffect(() => {
    if (!canCommissionStudents && applied.report === "commission") {
      setReport("general");
      resetPagination();
      if (searchParams.get("report") === "commission") {
        const next = new URLSearchParams(searchParams);
        next.set("report", "general");
        setSearchParams(next, { replace: true });
      }
    }
  }, [
    canCommissionStudents,
    applied.report,
    resetPagination,
    searchParams,
    setReport,
    setSearchParams,
  ]);

  useEffect(() => {
    const reportParam = searchParams.get("report");
    let mapped = mapReportParam(reportParam);
    if (mapped === "commission" && !canCommissionStudents) {
      mapped = "general";
    }
    if (!mapped) return;

    if (applied.report !== mapped) {
      setReport(mapped);
      resetPagination();
    }
  }, [
    searchParams,
    setSearchParams,
    setReport,
    resetPagination,
    applied.report,
    canCommissionStudents,
  ]);

  useEffect(() => {
    fetchProviders(draft.type).then(setProvidersList).catch(console.error);
  }, [draft.type]);

  useEffect(() => {
    fetchExternalAgents()
      .then(({ processing, referring }) => {
        setProcessingAgentsList(processing);
        setReferringAgentsList(referring);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetchBranches().then(setBranchesList).catch(console.error);
  }, []);

  useEffect(() => {
    if (draft.type === "migration" && draft.report !== "general") {
      setDraftField("report", "general");
    }
  }, [draft.type, draft.report, setDraftField]);

  useEffect(() => {
    if (!draft.providerId) {
      setDraftCoursesList([]);
      return;
    }
    let cancelled = false;
    fetchCoursesForProvider(draft.providerId)
      .then((courses) => {
        if (!cancelled) setDraftCoursesList(courses);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [draft.providerId]);

  useEffect(() => {
    setSelectedMap(new Map());
    setBatchModalOpen(false);
  }, [applied.report]);

  // Active filter count must NOT include search (search is outside filters)
  const activeFilterCount = useMemo(() => countActiveFilters(draft), [draft]);

  const selectedApplications = useMemo(
    () => Array.from(selectedMap.values()),
    [selectedMap],
  );

  const bulkSelectableApplications = useMemo(
    () =>
      selectedApplications.filter((application) =>
        isCommissionRowSelectableForBulk(application, applied.report),
      ),
    [selectedApplications, applied.report],
  );

  useEffect(() => {
    if (applied.report !== "commission") return;

    setSelectedMap((prev) => {
      let changed = false;
      const next = new Map(prev);

      for (const [key, application] of prev) {
        if (!isCommissionRowSelectableForBulk(application, "commission")) {
          next.delete(key);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [applications, applied.report, applied.commissionView]);

  const selectedKeys = useMemo(
    () => new Set(selectedMap.keys()),
    [selectedMap],
  );

  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchInput("");
    // Clear immediately (no need to wait for debounce)
    setAppliedSearchTerm("");
  }, []);

  const handleClear = useCallback(() => {
    // Reset filters only — search bar stays independent
    clearFilters();
    resetPagination();
  }, [clearFilters, resetPagination]);

  const handleTypeChange = useCallback(
    (type) => {
      setDraft((prev) => ({
        ...prev,
        type,
        report: type === "migration" ? "general" : prev.report,
        intakeId: type === "migration" ? null : prev.intakeId,
        providerId: null,
        courseId: null,
        processingAgentId: null,
        referringAgentId: null,
        statuses: [],
      }));
    },
    [setDraft],
  );

  const handleReportChange = useCallback(
    (report) => {
      if (report === "commission" && !canCommissionStudents) {
        return;
      }
      setReport(report);
      resetPagination();
      const next = new URLSearchParams(searchParams);
      next.set("report", report);
      setSearchParams(next, { replace: true });
    },
    [canCommissionStudents, setReport, resetPagination, setSearchParams, searchParams],
  );

  const handleExport = useCallback(() => {
    if (isInquiryBatchesTab) return;
    exportApplicationsCsv(applications);
  }, [applications, isInquiryBatchesTab]);

  const handleBlockedCommissionSelect = useCallback(async () => {
    await Swal.fire({
      icon: "info",
      title: "Cannot select closed case",
      text:
        "This commission case is closed. Closed rows cannot be included in bulk actions.",
      confirmButtonColor: "#007BFF",
    });
  }, []);

  const handleToggleSelect = useCallback(
    (application) => {
      if (!isCommissionRowSelectableForBulk(application, applied.report)) {
        handleBlockedCommissionSelect();
        return;
      }

      const key = getCommissionRowKey(application);

      setSelectedMap((prev) => {
        const next = new Map(prev);

        if (next.has(key)) {
          next.delete(key);
        } else {
          next.set(key, application);
        }

        return next;
      });
      setSelectionScope("page");
      setProviderSelectionMeta(null);
    },
    [applied.report, handleBlockedCommissionSelect],
  );

  const handleToggleSelectAll = useCallback((applications = []) => {
    setSelectionScope("page");
    setProviderSelectionMeta(null);
    setSelectedMap((prev) => {
      const next = new Map(prev);

      const keys = applications.map(getCommissionRowKey);

      const allSelected = keys.length > 0 && keys.every((key) => next.has(key));

      if (allSelected) {
        keys.forEach((key) => next.delete(key));
      } else {
        applications.forEach((application) => {
          next.set(getCommissionRowKey(application), application);
        });
      }

      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedMap(new Map());
    setSelectionScope("none");
    setProviderSelectionMeta(null);
  }, []);

  const handleInquiryProviderChange = useCallback(
    (providerId) => {
      setProviderFilter(providerId);
      resetPagination();
      clearSelection();
    },
    [setProviderFilter, resetPagination, clearSelection],
  );

  const handleSelectAllForProvider = useCallback(async () => {
    if (!applied.providerId || selectingAllProvider) return;

    setSelectingAllProvider(true);
    try {
      const allRows = await fetchAllApplicationsMatchingFilters(effectiveApplied);
      const next = new Map();
      let excluded = 0;

      allRows.forEach((application) => {
        if (!isCommissionRowSelectableForBulk(application, applied.report)) {
          excluded += 1;
          return;
        }
        next.set(getCommissionRowKey(application), application);
      });

      setSelectedMap(next);
      setSelectionScope("provider_all");
      setProviderSelectionMeta({
        providerId: applied.providerId,
        providerName: appliedProviderName,
        totalForProvider: pagination.total,
        selectedCount: next.size,
        excludedCount: excluded,
      });
    } catch (error) {
      showClientActionErrorToast(
        error?.message || "Failed to load all students for this provider.",
      );
    } finally {
      setSelectingAllProvider(false);
    }
  }, [
    applied.providerId,
    applied.report,
    appliedProviderName,
    effectiveApplied,
    pagination.total,
    selectingAllProvider,
  ]);

  const handleCommissionViewChange = useCallback(
    (view) => {
      setCommissionView(view);
      resetPagination();
      clearSelection();
    },
    [setCommissionView, resetPagination, clearSelection],
  );

  const handleBulkAddToCommission = useCallback(async () => {
    if (!selectedApplications.length) return;

    const confirmed = await confirmClientAction({
      clientName: `${selectedApplications.length} selected students`,
      type: "commission",
    });

    if (!confirmed) return;

    setActionLoadingKey("bulk-commission");

    try {
      await addApplicationsToQueue(selectedApplications, "commission");

      showClientAddedToast({
        clientName: `${selectedApplications.length} students`,
        type: "commission",
      });

      clearSelection();
      await refetch();
    } catch (error) {
      showClientActionErrorToast(error?.message);
    } finally {
      setActionLoadingKey(null);
    }
  }, [selectedApplications, clearSelection, refetch]);

  const handleAddToCommission = useCallback(
    async (application) => {
      const key = getCommissionRowKey(application);
      if (!key) return;

      const clientName = application?.clientname || "Client";
      const confirmed = await confirmClientAction({
        clientName,
        type: "commission",
      });
      if (!confirmed) return;

      setActionLoadingKey(`commission-${key}`);
      try {
        // Permanent commissionable membership (is_commissionable=true on backend)
        await addApplicationToQueue(application, "commission");
        showClientAddedToast({ clientName, type: "commission" });
        await refetch();
      } catch (error) {
        showClientActionErrorToast(error?.message);
      } finally {
        setActionLoadingKey(null);
      }
    },
    [refetch],
  );

  const handleRemoveFromCommissionable = useCallback(
    async (application) => {
      const key = getCommissionRowKey(application);
      const queueId = application?.queue_id;
      if (!key || !queueId) {
        showClientActionErrorToast("Queue record not found for this application.");
        return;
      }

      const clientName = application?.clientname || "Client";
      const confirmed = await confirmRemoveFromCommissionable({ clientName });
      if (!confirmed) return;

      setActionLoadingKey(`remove-commissionable-${key}`);
      try {
        await removeApplicationFromQueue(queueId);
        showClientRemovedFromCommissionableToast({ clientName });
        await refetch();
      } catch (error) {
        showClientActionErrorToast(error?.message);
      } finally {
        setActionLoadingKey(null);
      }
    },
    [refetch],
  );

  const handleDiscontinue = useCallback(
    async (application) => {
      const key = getCommissionRowKey(application);
      if (!key) return;

      const clientName = application?.clientname || "Client";
      const safeClientName = escapeHtml(clientName);
      const today = new Date().toISOString().slice(0, 10);

      const prompt = await Swal.fire({
        title: "",
        html: `
          <div class="cms-discontinue-modal__content">
            <div class="cms-discontinue-modal__hero">
              <div class="cms-discontinue-modal__badge" aria-hidden="true">!</div>
              <h3 class="cms-discontinue-modal__heading">Discontinued</h3>
              <p class="cms-discontinue-modal__subtitle">
                Update this application status to discontinued.
                <span class="cms-discontinue-modal__client">${safeClientName}</span>
              </p>
            </div>
            <div class="cms-discontinue-modal__form">
              <div class="cms-discontinue-modal__field">
                <label class="cms-discontinue-modal__label" for="commission-discontinue-note">
                  Note <span class="cms-discontinue-modal__required">*</span>
                </label>
                <textarea
                  id="commission-discontinue-note"
                  class="cms-discontinue-modal__textarea"
                  placeholder="Enter the reason for discontinuing this application..."
                  rows="4"
                ></textarea>
              </div>
              <div class="cms-discontinue-modal__field">
                <label class="cms-discontinue-modal__label" for="commission-discontinue-date">
                  Status change date
                </label>
                <input
                  id="commission-discontinue-date"
                  type="date"
                  class="cms-discontinue-modal__input"
                  value="${today}"
                />
                <p class="cms-discontinue-modal__hint">Defaults to today. Adjust if needed.</p>
              </div>
            </div>
          </div>
        `,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "Save changes",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        focusConfirm: false,
        customClass: {
          popup: "cms-discontinue-modal",
          htmlContainer: "cms-discontinue-modal__html",
          actions: "cms-discontinue-modal__actions",
          confirmButton:
            "cms-discontinue-modal__btn cms-discontinue-modal__btn--primary",
          cancelButton:
            "cms-discontinue-modal__btn cms-discontinue-modal__btn--secondary",
        },
        didOpen: () => {
          const noteField = document.getElementById("commission-discontinue-note");
          noteField?.focus();
        },
        preConfirm: () => {
          const note = document
            .getElementById("commission-discontinue-note")
            ?.value?.trim();
          const statusChangeDate =
            document.getElementById("commission-discontinue-date")?.value ||
            null;

          if (!note) {
            Swal.showValidationMessage("Please enter a note.");
            return false;
          }

          return { notes: note, statusChangeDate };
        },
      });

      if (!prompt.isConfirmed || !prompt.value) return;

      setActionLoadingKey(`discontinue-${key}`);

      try {
        await discontinueApplication(application, prompt.value);
        await Swal.fire({
          icon: "success",
          title: "Status updated",
          text: `${clientName} is now Discontinued.`,
          confirmButtonColor: "#007BFF",
        });
        if (applied.report === "commission") {
          setCommissionView("exceptions_reconciliation");
          resetPagination();
        }
        await refetch();
      } catch (error) {
        showClientActionErrorToast(error?.message);
      } finally {
        setActionLoadingKey(null);
      }
    },
    [applied.report, refetch, resetPagination, setCommissionView],
  );

  const handleReactivate = useCallback(
    async (application) => {
      const key = getCommissionRowKey(application);
      if (!key) return;

      const clientName = application?.clientname || "Client";
      const courseLabel = getCommissionCourseLabel(application);
      const targetLabel =
        courseLabel && courseLabel !== "-"
          ? `${clientName} — ${courseLabel}`
          : clientName;
      const { statusText } = getReactivateStatusMeta(application);

      const confirm = await Swal.fire({
        icon: "question",
        title: "Restore to Active?",
        html: `
          <p style="margin:0;font-size:14px;color:#334155;line-height:1.5">
            Restore <strong>${escapeHtml(targetLabel)}</strong> to
            <strong>${escapeHtml(statusText || "Active")}</strong>?
          </p>
          <p style="margin:12px 0 0;font-size:13px;color:#64748b">
            This restores the application to its previous status before Discontinued.
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: "Restore to Active",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#0284c7",
        reverseButtons: true,
      });

      if (!confirm.isConfirmed) return;

      setActionLoadingKey(`reactivate-${key}`);

      try {
        await reactivateApplication(application);
        await Swal.fire({
          icon: "success",
          title: "Restored to Active",
          text: `${targetLabel} is now ${statusText || "Active"}.`,
          confirmButtonColor: "#007BFF",
        });
        if (applied.report === "commission") {
          setCommissionView("active");
          resetPagination();
        }
        await refetch();
      } catch (error) {
        showClientActionErrorToast(error?.message);
      } finally {
        setActionLoadingKey(null);
      }
    },
    [applied.report, refetch, resetPagination, setCommissionView],
  );

  const handleMarkCompleted = useCallback(
    async (application) => {
      const key = getCommissionRowKey(application);
      if (!key) return;

      const clientName = application?.clientname || "Client";
      const courseLabel = getCommissionCourseLabel(application);
      const targetLabel =
        courseLabel && courseLabel !== "-"
          ? `${clientName} — ${courseLabel}`
          : clientName;

      const confirm = await Swal.fire({
        icon: "question",
        title: "Move to Completed?",
        html: `
          <p style="margin:0;font-size:14px;color:#334155;line-height:1.5">
            Mark <strong>${escapeHtml(targetLabel)}</strong> as completed —
            commission has been fully reconciled.
          </p>
          <p style="margin:12px 0 0;font-size:13px;color:#64748b">
            They will move to the <strong>Completed</strong> tab and disappear from Active and Exceptions & Reconciliation.
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: "Mark completed",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#059669",
        reverseButtons: true,
      });

      if (!confirm.isConfirmed) return;

      setActionLoadingKey(`completed-${key}`);

      try {
        await markCommissionStudentCompleted(application);
        await Swal.fire({
          icon: "success",
          title: "Moved to Completed",
          text: `${targetLabel} is now in the Completed tab.`,
          confirmButtonColor: "#007BFF",
        });
        await refetch();
      } catch (error) {
        showClientActionErrorToast(error?.message);
      } finally {
        setActionLoadingKey(null);
      }
    },
    [refetch],
  );

  const handleRevertCompleted = useCallback(
    async (application) => {
      const key = getCommissionRowKey(application);
      if (!key) return;

      const clientName = application?.clientname || "Client";
      const courseLabel = getCommissionCourseLabel(application);
      const targetLabel =
        courseLabel && courseLabel !== "-"
          ? `${clientName} — ${courseLabel}`
          : clientName;

      const confirm = await Swal.fire({
        icon: "question",
        title: "Move back to Active?",
        html: `
          <p style="margin:0;font-size:14px;color:#334155;line-height:1.5">
            Move <strong>${escapeHtml(targetLabel)}</strong> back to the
            <strong>Active</strong> tab?
          </p>
          <p style="margin:12px 0 0;font-size:13px;color:#64748b">
            Use this if they were marked completed by mistake. Their previous workflow status will be restored when available.
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: "Move to Active",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#007BFF",
        reverseButtons: true,
      });

      if (!confirm.isConfirmed) return;

      setActionLoadingKey(`revert-${key}`);

      try {
        await revertCommissionStudentCompleted(application);
        await Swal.fire({
          icon: "success",
          title: "Moved to Active",
          text: `${targetLabel} is back in the Active tab.`,
          confirmButtonColor: "#007BFF",
        });
        await refetch();
      } catch (error) {
        showClientActionErrorToast(error?.message);
      } finally {
        setActionLoadingKey(null);
      }
    },
    [refetch],
  );

  const handleSubmitFinalReview = useCallback(
    async (application) => {
      const key = getCommissionRowKey(application);
      if (!key) return;

      const clientName = application?.clientname || "Client";
      const courseLabel = getCommissionCourseLabel(application);
      const targetLabel =
        courseLabel && courseLabel !== "-"
          ? `${clientName} — ${courseLabel}`
          : clientName;

      const confirm = await Swal.fire({
        icon: "question",
        title: "Send for final review?",
        html: `
          <p style="margin:0;font-size:14px;color:#334155;line-height:1.5">
            Send <strong>${escapeHtml(targetLabel)}</strong> to the
            <strong>Final Review</strong> queue for sign-off.
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: "Send for review",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#7C3AED",
        reverseButtons: true,
      });

      if (!confirm.isConfirmed) return;

      setActionLoadingKey(`submit-review-${key}`);

      try {
        await submitCommissionFinalReview(application);
        await Swal.fire({
          icon: "success",
          title: "Sent for final review",
          text: `${targetLabel} is in the Final Review tab.`,
          confirmButtonColor: "#007BFF",
        });
        await refetch();
      } catch (error) {
        showClientActionErrorToast(error?.message);
      } finally {
        setActionLoadingKey(null);
      }
    },
    [refetch],
  );

  const handleApproveFinalReview = useCallback(
    async (application) => {
      const key = getCommissionRowKey(application);
      if (!key) return;

      const clientName = application?.clientname || "Client";
      const courseLabel = getCommissionCourseLabel(application);
      const targetLabel =
        courseLabel && courseLabel !== "-"
          ? `${clientName} — ${courseLabel}`
          : clientName;

      const confirm = await Swal.fire({
        icon: "question",
        title: "Approve and close?",
        html: `
          <p style="margin:0;font-size:14px;color:#334155;line-height:1.5">
            Approve <strong>${escapeHtml(targetLabel)}</strong> and mark the commission case
            <strong>closed</strong>. It will remain visible under <strong>All</strong>.
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: "Approve & close",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#059669",
        reverseButtons: true,
      });

      if (!confirm.isConfirmed) return;

      setActionLoadingKey(`approve-review-${key}`);

      try {
        await approveCommissionFinalReview(application);
        await Swal.fire({
          icon: "success",
          title: "Approved and closed",
          text: `${targetLabel} is closed.`,
          confirmButtonColor: "#007BFF",
        });
        await refetch();
      } catch (error) {
        showClientActionErrorToast(error?.message);
      } finally {
        setActionLoadingKey(null);
      }
    },
    [refetch],
  );

  const handleBulkWorkflowAction = useCallback(
    async (actionMeta) => {
      if (!actionMeta?.id || !actionMeta.eligibleApplications?.length) {
        return;
      }

      const { id: actionId, eligibleApplications, label, confirmTitle } =
        actionMeta;
      const count = eligibleApplications.length;

      let notes = null;
      let statusChangeDate = null;

      if (actionMeta.requiresDiscontinueForm) {
        const today = new Date().toISOString().slice(0, 10);
        const prompt = await Swal.fire({
          title: "",
          html: `
          <div class="cms-discontinue-modal__content">
            <div class="cms-discontinue-modal__hero">
              <div class="cms-discontinue-modal__badge" aria-hidden="true">!</div>
              <h3 class="cms-discontinue-modal__heading">Discontinued</h3>
              <p class="cms-discontinue-modal__subtitle">
                Update <strong>${count}</strong> selected application(s) to discontinued.
              </p>
            </div>
            <div class="cms-discontinue-modal__form">
              <div class="cms-discontinue-modal__field">
                <label class="cms-discontinue-modal__label" for="commission-bulk-discontinue-note">
                  Note <span class="cms-discontinue-modal__required">*</span>
                </label>
                <textarea
                  id="commission-bulk-discontinue-note"
                  class="cms-discontinue-modal__textarea"
                  placeholder="Enter the reason for discontinuing..."
                  rows="4"
                ></textarea>
              </div>
              <div class="cms-discontinue-modal__field">
                <label class="cms-discontinue-modal__label" for="commission-bulk-discontinue-date">
                  Status change date
                </label>
                <input
                  id="commission-bulk-discontinue-date"
                  type="date"
                  class="cms-discontinue-modal__input"
                  value="${today}"
                />
              </div>
            </div>
          </div>
        `,
          showCloseButton: true,
          showCancelButton: true,
          confirmButtonText: "Save changes",
          cancelButtonText: "Cancel",
          reverseButtons: true,
          focusConfirm: false,
          customClass: {
            popup: "cms-discontinue-modal",
            htmlContainer: "cms-discontinue-modal__html",
            actions: "cms-discontinue-modal__actions",
            confirmButton:
              "cms-discontinue-modal__btn cms-discontinue-modal__btn--primary",
            cancelButton:
              "cms-discontinue-modal__btn cms-discontinue-modal__btn--secondary",
          },
          preConfirm: () => {
            const note = document
              .getElementById("commission-bulk-discontinue-note")
              ?.value?.trim();
            const date =
              document.getElementById("commission-bulk-discontinue-date")
                ?.value || null;
            if (!note) {
              Swal.showValidationMessage("Please enter a note.");
              return false;
            }
            return { notes: note, statusChangeDate: date };
          },
        });

        if (!prompt.isConfirmed || !prompt.value) return;
        notes = prompt.value.notes;
        statusChangeDate = prompt.value.statusChangeDate;
      } else {
        const confirm = await Swal.fire({
          icon: "question",
          title: confirmTitle || `Run bulk action?`,
          html: `
            <p style="margin:0;font-size:14px;color:#334155;line-height:1.5">
              Apply <strong>${escapeHtml(label)}</strong> to
              <strong>${count}</strong> selected student(s)?
            </p>
          `,
          showCancelButton: true,
          confirmButtonText: "Continue",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#007BFF",
          reverseButtons: true,
        });

        if (!confirm.isConfirmed) return;
      }

      setActionLoadingKey(`bulk-${actionId}`);

      try {
        const result = await runCommissionBulkWorkflow({
          action: actionId,
          applications: eligibleApplications,
          commissionView: applied.commissionView || "active",
          notes,
          statusChangeDate,
        });

        const data = result?.data || {};
        const succeeded = data.succeeded?.length ?? 0;
        const failed = data.failed?.length ?? 0;
        const skipped = data.skipped?.length ?? 0;

        await Swal.fire({
          icon: failed > 0 ? "warning" : "success",
          title: failed > 0 ? "Bulk action completed with errors" : "Bulk action complete",
          html: `
            <p style="margin:0;font-size:14px;color:#334155">
              <strong>${succeeded}</strong> succeeded,
              <strong>${failed}</strong> failed,
              <strong>${skipped}</strong> skipped (not eligible).
            </p>
          `,
          confirmButtonColor: "#007BFF",
        });

        if (actionId === "discontinue" && succeeded > 0 && applied.report === "commission") {
          setCommissionView("exceptions_reconciliation");
          resetPagination();
        }

        clearSelection();
        await refetch();
      } catch (error) {
        showClientActionErrorToast(error?.message);
      } finally {
        setActionLoadingKey(null);
      }
    },
    [
      applied.commissionView,
      applied.report,
      clearSelection,
      refetch,
      resetPagination,
      setCommissionView,
    ],
  );

  const handleReturnFromFinalReview = useCallback(
    async (application) => {
      const key = getCommissionRowKey(application);
      if (!key) return;

      const clientName = application?.clientname || "Client";
      const courseLabel = getCommissionCourseLabel(application);
      const targetLabel =
        courseLabel && courseLabel !== "-"
          ? `${clientName} — ${courseLabel}`
          : clientName;

      const fromCompleted =
        String(application?.final_review_origin || "").trim() === "completed";
      const returnTabLabel = fromCompleted ? "Completed" : "Exceptions";

      const confirm = await Swal.fire({
        icon: "question",
        title: fromCompleted ? "Restore to Completed?" : "Restore to Exceptions?",
        html: `
          <p style="margin:0;font-size:14px;color:#334155;line-height:1.5">
            Move <strong>${escapeHtml(targetLabel)}</strong> back to the
            <strong>${escapeHtml(returnTabLabel)}</strong> tab?
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: "Restore",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#007BFF",
        reverseButtons: true,
      });

      if (!confirm.isConfirmed) return;

      setActionLoadingKey(`return-review-${key}`);

      try {
        const result = await returnCommissionFromFinalReview(application);
        await Swal.fire({
          icon: "success",
          title: "Restored",
          text:
            result?.message ||
            `${targetLabel} is back in ${returnTabLabel}.`,
          confirmButtonColor: "#007BFF",
        });
        await refetch();
      } catch (error) {
        showClientActionErrorToast(error?.message);
      } finally {
        setActionLoadingKey(null);
      }
    },
    [refetch],
  );

  const handleBatchCreated = useCallback(
    async (result) => {
      setBatchModalOpen(false);
      clearSelection();
      const batch = result?.batch ?? result;
      const skippedCount = result?.skipped_count ?? 0;
      const label =
        batch?.batch_name || batch?.batch_no || "Inquiry batch";
      const skippedNote =
        skippedCount > 0
          ? ` ${skippedCount} student(s) were skipped because they already have an inquiry.`
          : "";
      await Swal.fire({
        icon: "success",
        title: "Batch created",
        text: `"${label}" has been created successfully.${skippedNote}`,
        confirmButtonText: "OK",
        confirmButtonColor: "#007BFF",
      });
      setReport("inquiry_batches");
      setSearchParams({ report: "inquiry_batches" }, { replace: true });
    },
    [clearSelection, setReport, setSearchParams],
  );

  const handlePaginationCallback = useCallback(
    (page, perPage) => {
      if (perPage && perPage !== "") {
        handlePerPageChange(perPage);
      } else {
        handlePageChange({ selected: page - 1 });
      }
    },
    [handlePageChange, handlePerPageChange],
  );

  const showingCount = applications?.length ?? 0;

  if (!canViewCommissionModule()) {
    return (
      <div className="tailwind-container p-4">
        <p className="text-muted mb-0">
          You do not have permission to view the commission module.
        </p>
      </div>
    );
  }

  return (
    <div className="tailwind-container">
      <div
        className={`${TASK_PAGE_SHELL_CLASS} cms-commissions-shell`}
        style={MODULE_PAGE_SHELL_STYLE}
        aria-busy={loading}
      >
        {/* Automation rules hidden — http://localhost:3000/commissions-invoices (Commissionable Students); was showAutomationRules={isCommissionTab} */}
        <CommissionsHeader
          onExport={handleExport}
          loading={loading}
          showInquiryBatches={false}
          showAutomationRules={false}
        />

        <div className="mt-3">
          <CommissionsReportTabs
            value={applied.report}
            onChange={handleReportChange}
            visible={applied.type === "education"}
            showCommissionStudents={canCommissionStudents}
          />
        </div>

        {isInquiryBatchesTab ? (
          <div className="mt-3">
            <InquiryBatchesPanel
              applicationType={applied.type || "education"}
            />
          </div>
        ) : (
          <>
            <div className="mt-3">
              <CommissionsSearchFilters
                type={draft.type}
                onTypeChange={handleTypeChange}
                searchTerm={searchInput}
                onSearchChange={handleSearchChange}
                onSearchClear={handleSearchClear}
                filtersOpen={filtersOpen}
                onToggleFilters={() => setFiltersOpen((p) => !p)}
                activeFilterCount={activeFilterCount}
                draft={draft}
                applied={applied}
                onDraftChange={setDraft}
                providersList={providersList}
                coursesList={draftCoursesList}
                intakes={intakes}
                processingAgentsList={processingAgentsList}
                referringAgentsList={referringAgentsList}
                processingBranchesList={branchesList}
                referringBranchesList={branchesList}
                onReset={handleClear}
                onRemoveChip={removeChip}
                suppressProviderFilter={isCommissionTab}
              />
            </div>
            {isCommissionTab ? (
              <div className="mt-3">
                <CommissionsQueueTabs
                  value={applied.commissionView || "active"}
                  counts={viewCounts}
                  onChange={handleCommissionViewChange}
                />
              </div>
            ) : null}
            <div className="mt-3 overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
                {isCommissionTab ? (
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    <div className="w-full min-w-[200px] max-w-[280px] shrink-0">
                      <CommissionsSingleSelect
                        label=""
                        value={applied.providerId}
                        onChange={handleInquiryProviderChange}
                        options={providersList}
                        placeholder="Provider for inquiry…"
                        searchPlaceholder="Search providers…"
                      />
                    </div>
                    {applied.providerId &&
                    canCreateInquiryBatch &&
                    pagination.total > 0 &&
                    selectionScope !== "provider_all" ? (
                      <button
                        type="button"
                        disabled={selectingAllProvider || loading}
                        onClick={handleSelectAllForProvider}
                        title={`Select all ${pagination.total} students for ${appliedProviderName || "this provider"}`}
                        className="inline-flex h-8 shrink-0 items-center px-1 text-[12px] font-medium text-[#007BFF] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                        style={{
                          border: "none",
                          boxShadow: "none",
                          background: "transparent",
                        }}
                      >
                        {selectingAllProvider
                          ? "Selecting…"
                          : `Select all (${pagination.total})`}
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex-1" />
                )}
                <div className="flex flex-wrap items-center justify-end gap-2">
                {selectedApplications.length > 0 ? (
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 text-[12px] font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                    style={{ boxShadow: "none" }}
                  >
                    <FaTimes size={11} />
                    Clear ({selectedApplications.length})
                  </button>
                ) : null}
                {isCommissionTab && bulkSelectableApplications.length > 0 ? (
                  <CommissionsBulkActions
                    selectedApplications={bulkSelectableApplications}
                    commissionView={applied.commissionView || "active"}
                    activeReport={applied.report}
                    loading={Boolean(
                      actionLoadingKey &&
                        String(actionLoadingKey).startsWith("bulk-"),
                    )}
                    onRunAction={handleBulkWorkflowAction}
                  />
                ) : null}
                {canCommissionStudents &&
                !isCommissionTab &&
                selectedApplications.length > 0 ? (
                  <button
                    type="button"
                    onClick={handleBulkAddToCommission}
                    disabled={actionLoadingKey === "bulk-commission"}
                    title="Move selected applications into Commissionable Students."
                    className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#007BFF] px-2.5 text-[12px] font-semibold text-white hover:bg-[#0069D9] disabled:cursor-not-allowed disabled:opacity-45"
                    style={{ border: "none", boxShadow: "none" }}
                  >
                    Move to Commissionable ({selectedApplications.length})
                  </button>
                ) : null}
                {isCommissionTab && canCreateInquiryBatch ? (
                  <button
                    type="button"
                    disabled={bulkSelectableApplications.length === 0}
                    title={
                      applied.providerId
                        ? "Select all students for this provider, or pick rows, then create the batch."
                        : "Pick a provider to filter the table, or select rows, then create the batch."
                    }
                    onClick={() => setBatchModalOpen(true)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-45"
                    style={{ border: "none", boxShadow: "none" }}
                  >
                    <FaPaperPlane size={11} />
                    Create inquiry batch
                    {bulkSelectableApplications.length > 0
                      ? ` (${bulkSelectableApplications.length})`
                      : ""}
                  </button>
                ) : null}
                <CommissionsColumnSettings
                  report={columnReport}
                  visibleKeys={visibleKeys}
                  onChange={setVisibleKeys}
                />
                </div>
              </div>
              <CommissionsTable
                applications={applications}
                loading={loading}
                sortBy={applied.sortBy}
                sortDir={applied.sortDir}
                onSort={toggleSort}
                embedded
                activeReport={applied.report}
                commissionView={applied.commissionView || "active"}
                actionLoadingKey={actionLoadingKey}
                onAddToCommission={
                  canCommissionStudents ? handleAddToCommission : undefined
                }
                onRemoveFromCommissionable={
                  canRemoveFromCommissionableStudents()
                    ? handleRemoveFromCommissionable
                    : undefined
                }
                onDiscontinue={handleDiscontinue}
                onReactivate={handleReactivate}
                onMarkCompleted={handleMarkCompleted}
                onRevertCompleted={handleRevertCompleted}
                onSubmitFinalReview={handleSubmitFinalReview}
                onApproveFinalReview={handleApproveFinalReview}
                onReturnFromFinalReview={handleReturnFromFinalReview}
                selectedKeys={selectedKeys}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                onBlockedCommissionSelect={handleBlockedCommissionSelect}
                onEditInquiry={setEditApplication}
                visibleColumns={visibleColumns}
                providerFilterActive={Boolean(applied.providerId)}
              />

              {!loading && pagination.total > 0 && (
                <div className="border-t border-gray-100 px-2 py-1">
                  <Paginations
                    currentPage={pagination.currentPage}
                    itemsPerPage={pagination.perPage}
                    lastPage={pagination.lastPage}
                    onPageChange={handlePaginationCallback}
                    totalItems={pagination.total}
                    perItem={showingCount}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <CreateInquiryBatchModal
        open={batchModalOpen}
        applications={
          isCommissionTab ? bulkSelectableApplications : selectedApplications
        }
        applicationType={applied.type || "education"}
        providerLabel={appliedProviderName}
        onClose={() => setBatchModalOpen(false)}
        onCreated={handleBatchCreated}
      />

      <InquiryFillModal
        open={Boolean(editApplication)}
        application={editApplication}
        validationMode="batch"
        title="Edit inquiry details"
        mode="fill"
        onClose={() => setEditApplication(null)}
        onSaved={async () => {
          setEditApplication(null);
          await refetch();
        }}
      />
    </div>
  );
}

export default CommissionsPage;